import React, { useState } from 'react';
import styled from 'styled-components';
import { Image } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import Breakpoints from '../../design-utils/breakpoints';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { useQuery } from 'react-query';
import currencyFormatter from 'currency-formatter';
import {
  getLastOrderId,
  getLastOrderDetails,
  getResourcesImages,
  formatOrderData,
} from './utils';
import ItemizationDetail from './Itemization';
import { useGlobalContext } from '../state/GlobalContext';

export const ProductImage = ({
  src,
  itemQty,
}: {
  src: string;
  itemQty: number;
}) =>
  !isEmpty(src) ? (
    <ImageContainer>
      {itemQty > 1 && <QtyBadge>{itemQty}</QtyBadge>}
      <Image boxSize="32px" borderRadius="8px" src={src} />
    </ImageContainer>
  ) : (
    <ImageContainer>
      {itemQty > 1 && <QtyBadge>{itemQty}</QtyBadge>}
      <DefaultImage />
    </ImageContainer>
  );

const LastOrder: React.FC = (): JSX.Element => {
  const { customerId, subscriptionId, authToken } = useGlobalContext();
  const { t } = useTranslation();
  const [displayItemization, setDisplayItemization] = useState(false);
  const lastOrderId = getLastOrderId();

  const handleDisplayItemization = () => {
    setDisplayItemization(!displayItemization);
  };

  const { data: lastOrder } = useQuery(
    ['last-order-details', customerId],
    async () => {
      const data = await getLastOrderDetails(lastOrderId, authToken);
      return formatOrderData(data);
    },
    {
      enabled: Boolean(authToken && lastOrderId),
    },
  );

  const { data: lastOrderImages } = useQuery(
    ['last-order-images', customerId],
    async () => {
      const images = await getResourcesImages(
        customerId,
        subscriptionId,
        Object.keys(resourcesIds),
        authToken,
      );
      return images;
    },
    {
      enabled: Boolean(lastOrder),
    },
  );

  if (isEmpty(lastOrder)) {
    return null;
  }

  const { totalPrice, orderDate, isFulfilled, resourcesIds = {} } = lastOrder;
  const fulfilledText = isFulfilled
    ? t('lastOrder.fulfilledStatus')
    : t('lastOrder.unfulfilledStatus');

  return (
    <PreviousOrder>
      <TitleContainer>
        <TitleInfo>
          <Title>{t('lastOrder.title', { orderDate })}</Title>
          <Badge>{fulfilledText}</Badge>
        </TitleInfo>
        <ItemizationButton onClick={handleDisplayItemization}>
          {displayItemization ? (
            <ChevronUpIcon boxSize="8" />
          ) : (
            <ChevronDownIcon boxSize="8" />
          )}
        </ItemizationButton>
      </TitleContainer>
      <OrderItemization isDisplayed={displayItemization}>
        <ItemizationDetail order={lastOrder} images={lastOrderImages} />
      </OrderItemization>
      <OrderSummary isDisplayed={!displayItemization}>
        <ImagesGallery>
          {Object.keys(resourcesIds).map((resource) => {
            const itemQty = resourcesIds[resource];
            return (
              <ProductImage
                src={lastOrderImages?.[resource]}
                itemQty={itemQty}
                key={resource}
              />
            );
          })}
        </ImagesGallery>
        <OrderTotal>
          {currencyFormatter.format(totalPrice.amount, {
            code: totalPrice.currency,
          })}
        </OrderTotal>
      </OrderSummary>
    </PreviousOrder>
  );
};

const PreviousOrder = styled.section.attrs({
  className: 'awt-last-order-container',
})`
  border: 1px solid #dfe3e8;
  padding: 24px;
  padding-bottom: 16px;
  border-radius: 4px;
  margin: 20px 0;
`;
const TitleContainer = styled.div.attrs({
  className: 'awt-last-order-title-container',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const TitleInfo = styled.div.attrs({
  className: 'awt-last-order-title-info',
})`
  display: flex;
  align-items: center;

  @media (max-width: ${Breakpoints.sm}) {
    display: block;
    align-items: left;
  }
`;
const Title = styled.div.attrs({
  className: 'awt-last-order-title',
})`
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
`;
const ItemizationButton = styled.div.attrs({
  className: 'awt-order-itemization-button',
})`
  cursor: pointer;
  background-color: #f6f6f7;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const OrderItemization = styled.div.attrs({
  className: 'awt-order-itemization-container',
})<{
  readonly isDisplayed?: boolean;
}>`
  max-height: ${({ isDisplayed }) => (isDisplayed ? '365px' : '0px')};
  visibility: ${({ isDisplayed }) => (isDisplayed ? 'visible' : 'hidden')};
  transition: all 0.3s ease-in-out;
  overflow: hidden;
`;
const Badge = styled.div.attrs({ className: 'awt-last-order-badge' })`
  font-size: 12px;
  padding: 8px 16px;
  margin-left: 12px;
  border-radius: 20px;
  background-color: #f6f6f7;

  @media (max-width: ${Breakpoints.sm}) {
    margin-left: 0;
    float: left;
    margin-top: 8px;
  }
`;
const OrderSummary = styled.div.attrs({
  className: 'awt-order-summary',
})<{
  readonly isDisplayed?: boolean;
}>`
  display: ${({ isDisplayed }) => (isDisplayed ? 'flex' : 'none')};
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;
const QtyBadge = styled.div.attrs({
  className: 'awt-qty-badge',
})`
  position: absolute;
  left: 20px;
  bottom: 20px;
  background: #000000;
  text-align: center;
  border-radius: 50%;
  color: white;
  font-size: 12px;
  width: 16px;
  line-height: 16px;
  height: 16px;
  z-index: 1;
  font-weight: bold;
  animation: 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
`;
const ImageContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;
const ImagesGallery = styled.div.attrs({
  className: 'awt-order-images-gallery',
})`
  position: relative;
  display: flex;
  gap: 8px;
  width: 100%;
  overflow-x: hidden;
  padding-top: 4px;

  &:after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background: linear-gradient(90deg, transparent 30%, #fff 100%);
  }
`;
const DefaultImage = styled.div`
  height: 32px;
  width: 32px;
  border-radius: 8px;
  background-color: #dfe3e8;
`;
const OrderTotal = styled.div.attrs({ className: 'awt-order-total' })`
  font-size: 16px;
  font-weight: 700;
`;

export default LastOrder;
